/**
 * PluginDevPreviewPage — Load a plugin from a local dev server inside an iframe
 * with a real PluginBridgeHost connected. All SDK hooks work against the live
 * platform (real auth, real storage, real entity invocation).
 *
 * URL: /a/arche_plugins/dev-preview?url=http://localhost:3100&permissions=theme:read,storage:sandbox
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { RefreshCw, AlertCircle, ExternalLink, Shield, Terminal, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/auth/hooks/useAuth';
import { toast } from '@/components/ui/use-toast';
import { PluginBridgeHost } from '../../../components/PluginBridge';
import { usePluginTextModelCatalog } from '../../../components/plugin-text-models';
import { PLUGIN_IFRAME_ALLOW, PLUGIN_IFRAME_SANDBOX } from '../../../components/pluginSandbox';
import {
  DARK_THEME,
  LIGHT_THEME,
  VALID_PLUGIN_PERMISSIONS,
  type PluginPermission,
} from '@fias/arche-sdk';
import { API_BASE } from '@/lib/api';

interface ConsoleEntry {
  timestamp: number;
  level: 'info' | 'warn' | 'error';
  message: string;
}

/**
 * Restrict dev-preview URLs to localhost.
 *
 * The previous implementation allowed *any* https: URL on the theory
 * that "if you're typing this URL you know what you're doing." But the
 * page is reachable via simple GET, so a phishing link of the shape
 * `/a/arche_plugins/dev-preview?url=https://attacker.example/plugin
 *  &permissions=entities:invoke,storage:sandbox` was enough to load
 * attacker code into a real-bridge iframe with the victim's auth
 * token. The route itself is now dev-build-only (see App.tsx), but we
 * keep this check as defense in depth: even in dev, the URL must
 * resolve to localhost.
 */
function isValidDevUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1';
  } catch {
    return false;
  }
}

function parsePermissions(raw: string | null): PluginPermission[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((p): p is PluginPermission => VALID_PLUGIN_PERMISSIONS.includes(p as PluginPermission));
}

export function PluginDevPreviewPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const pluginUrl = searchParams.get('url');
  const permissions = parsePermissions(searchParams.get('permissions'));

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const bridgeRef = useRef<PluginBridgeHost | null>(null);
  const { theme: appTheme } = useTheme();
  const themeMode = appTheme === 'light' ? 'light' : 'dark';
  // Latest-ref for getTheme — see PluginRenderer's themeModeRef for the full
  // rationale (the bridge is built once per iframe load; a closure would
  // freeze the mode of that render).
  const themeModeRef = useRef(themeMode);
  themeModeRef.current = themeMode;

  const [iframeHeight, setIframeHeight] = useState(600);
  const [iframeKey, setIframeKey] = useState(0);
  const [consoleOpen, setConsoleOpen] = useState(false);
  const [consoleEntries, setConsoleEntries] = useState<ConsoleEntry[]>([]);

  const addConsoleEntry = useCallback((level: ConsoleEntry['level'], message: string) => {
    setConsoleEntries((prev) => [
      ...prev.slice(-199), // Keep last 200 entries
      { timestamp: Date.now(), level, message },
    ]);
  }, []);

  // Validate URL
  const urlError = !pluginUrl
    ? 'Missing "url" query parameter. Usage: ?url=http://localhost:3100&permissions=theme:read'
    : !isValidDevUrl(pluginUrl)
      ? 'URL must point to localhost (this preview is local-development-only).'
      : null;

  // The text-model catalog (useTextModels), same source as the published
  // host. Read through a ref so the bridge built at iframe load sees the list
  // once it loads, and pushed then (the SDK ignores a repeated init).
  const textModels = usePluginTextModelCatalog(permissions.includes('entities:invoke'));
  const textModelsRef = useRef(textModels);
  textModelsRef.current = textModels;
  useEffect(() => {
    if (textModels) bridgeRef.current?.sendTextModelsUpdate();
  }, [textModels]);

  // Set up bridge when iframe loads
  const handleIframeLoad = useCallback(() => {
    if (!iframeRef.current || !pluginUrl) return;

    // Clean up previous bridge
    bridgeRef.current?.destroy();

    addConsoleEntry('info', `Bridge connecting to ${pluginUrl}`);

    bridgeRef.current = new PluginBridgeHost({
      archId: 'dev_preview',
      permissions,
      // Via the ref — see textModelsRef.
      getTextModels: () => textModelsRef.current,
      iframe: iframeRef.current,
      onResize: (height) => {
        setIframeHeight(height);
        addConsoleEntry('info', `Plugin resized to ${height}px`);
      },
      onToast: (message, variant) => {
        toast({
          description: message,
          variant: variant === 'error' ? 'destructive' : undefined,
        });
        addConsoleEntry('info', `Toast: [${variant}] ${message}`);
      },
      onNavigate: (path, { replace }) => {
        navigate(path, { replace });
        addConsoleEntry('info', `Navigate${replace ? ' (replace)' : ''}: ${path}`);
      },
      getTheme: () => {
        // Via the ref (React state), NOT a DOM read. The push effect below
        // fires child-first — often BEFORE next-themes' provider effect has
        // restamped the documentElement class — so a DOM read here would push
        // a theme one toggle behind. Same live-ref idiom as PluginRenderer.
        return themeModeRef.current === 'dark' ? DARK_THEME : LIGHT_THEME;
      },
      getUser: async () => ({
        userId: user?.userId ?? '',
        displayName: user?.username ?? 'User',
        avatar: null,
      }),
      bridgeApiUrl: API_BASE,
      // The dev preview mounts an arbitrary plugin URL with no registered
      // arche, so no per-arche bridge capability token can be minted. Rather
      // than emit an empty bearer that the bridge rejects as a cryptic
      // "JWT … 3 parts" 401 (the old `getPluginBridgeToken('arche_plugin_builder')
      // ?? ''` hack — that arche is renderMode 'default', so the mint always
      // 400'd), fail loudly. Dev-only route; a real preview-token issuer is the
      // proper future fix.
      getAuthToken: async () => {
        throw new Error(
          'Dev preview cannot mint a bridge token: this plugin has no registered arche. Bridge operations are unavailable in dev preview.',
        );
      },
    });

    bridgeRef.current.init();
    addConsoleEntry(
      'info',
      `Bridge initialized with permissions: ${permissions.join(', ') || 'none'}`,
    );
  }, [pluginUrl, permissions, navigate, user, addConsoleEntry]);

  // Push theme updates to the plugin when the app theme toggles. Nothing was
  // telling the plugin a change had happened — a dev-preview plugin only
  // re-pulled the theme on its next full reload, which made the preview lie
  // about the live-switch behavior a published plugin gets from
  // PluginRenderer. The bridge is null until the iframe loads; toggles before
  // that are a no-op and init delivers the then-current theme anyway.
  useEffect(() => {
    bridgeRef.current?.sendThemeUpdate();
  }, [themeMode]);

  // Clean up bridge on unmount
  useEffect(() => {
    return () => {
      bridgeRef.current?.destroy();
    };
  }, []);

  const handleReload = useCallback(() => {
    bridgeRef.current?.destroy();
    setIframeKey((k) => k + 1);
    addConsoleEntry('info', 'Plugin reloaded');
  }, [addConsoleEntry]);

  if (urlError) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Dev Preview Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{urlError}</p>
            <p className="mt-4 text-sm text-muted-foreground">
              Example:{' '}
              <code className="bg-muted px-2 py-1 rounded text-xs">
                /a/arche_plugins/dev-preview?url=http://localhost:3100&permissions=theme:read,storage:sandbox
              </code>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Dev toolbar */}
      <div className="flex items-center gap-3 px-4 py-2 border-b bg-muted/50 shrink-0">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <ExternalLink className="h-4 w-4" />
          <span className="font-mono text-xs">{pluginUrl}</span>
        </div>

        <Button variant="ghost" size="sm" onClick={handleReload} title="Reload plugin">
          <RefreshCw className="h-4 w-4" />
        </Button>

        <div className="flex-1" />

        <div className="flex items-center gap-1.5">
          <Shield className="h-3.5 w-3.5 text-muted-foreground" />
          {permissions.length === 0 ? (
            <span className="text-xs text-muted-foreground">No permissions</span>
          ) : (
            permissions.map((p) => (
              <Badge key={p} variant="secondary" className="text-xs">
                {p}
              </Badge>
            ))
          )}
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => setConsoleOpen((v) => !v)}
          title="Toggle console"
          className={consoleOpen ? 'text-primary' : ''}
        >
          <Terminal className="h-4 w-4" />
        </Button>
      </div>

      {/* Plugin iframe */}
      <div className="flex-1 overflow-auto">
        <iframe
          key={iframeKey}
          ref={iframeRef}
          src={pluginUrl!}
          onLoad={handleIframeLoad}
          sandbox={PLUGIN_IFRAME_SANDBOX}
          allow={PLUGIN_IFRAME_ALLOW}
          referrerPolicy="no-referrer"
          style={{
            width: '100%',
            height: `${iframeHeight}px`,
            border: 'none',
            display: 'block',
          }}
          title="Plugin Dev Preview"
        />
      </div>

      {/* Console panel */}
      {consoleOpen && (
        <div className="border-t bg-background shrink-0" style={{ height: '200px' }}>
          <div className="flex items-center justify-between px-3 py-1.5 border-b bg-muted/30">
            <span className="text-xs font-medium text-muted-foreground">Bridge Console</span>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={() => setConsoleEntries([])}
              >
                Clear
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={() => setConsoleOpen(false)}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
          <div className="overflow-auto h-[calc(100%-32px)] font-mono text-xs p-2 space-y-0.5">
            {consoleEntries.length === 0 ? (
              <p className="text-muted-foreground">No bridge messages yet.</p>
            ) : (
              consoleEntries.map((entry, i) => (
                <div
                  key={i}
                  className={
                    entry.level === 'error'
                      ? 'text-destructive'
                      : entry.level === 'warn'
                        ? 'text-yellow-600 dark:text-yellow-400'
                        : 'text-muted-foreground'
                  }
                >
                  <span className="opacity-50">
                    {new Date(entry.timestamp).toLocaleTimeString()}
                  </span>{' '}
                  {entry.message}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
